import React from 'react';
import { ReactComponent as LapturaMark } from '../../assets/brand/laptura-mark.svg';
import { ReactComponent as LapturaWordmark } from '../../assets/brand/laptura-wordmark.svg';
import imsaLogo from '../../assets/imsa-logo.png';
import mx5Logo from '../../assets/mx5-cup-logo.png';
import { useAccentTheme, getAccentBrand } from '../../contexts/AccentThemeContext';
import { cn } from '../../lib/utils';

/**
 * Series id -> licensed logo artwork.
 *
 * `layout` is about the artwork's shape, not the series:
 *   'strip'   wide one-line lockup (IMSA, 4.8:1) — fits the wordmark line at
 *             any size, tagline sits underneath.
 *   'stacked' tall multi-line lockup (MX-5 Cup, 1.5:1) — unreadable at wordmark
 *             height, so it gets the full header height with the tagline beside
 *             it, and drops to the type-set wordmark in the 40px mobile bar.
 */
const SERIES_LOGOS = {
  imsa: { src: imsaLogo, layout: 'strip' },
  mx5: { src: mx5Logo, layout: 'stacked' },
};

/**
 * App wordmark for the sidebar / mobile header.
 *
 * Stock Laptura is the full wordmark logo (icon-only mark when compact,
 * since the wordmark is illegible at that height). A series theme (see
 * ACCENT_THEMES[*].brand) swaps in a "<SERIES> powered by Laptura" lockup.
 *
 * Series logos are licensed artwork used under agreement (IMSA / MX-5 Cup
 * pilot). They are rendered unmodified — no recolor, no stroke, no distortion —
 * per the brand books' logo-integrity rules; only the display size changes. A
 * series without usable artwork falls back to a type-set wordmark.
 */
export function BrandMark({ compact = false, className }) {
  const { accent } = useAccentTheme();
  const brand = getAccentBrand(accent);
  const logo = SERIES_LOGOS[accent];

  if (!brand) {
    return (
      <div className={cn('flex items-center text-lap-text', className)}>
        {compact ? (
          <LapturaMark className="h-8 w-auto" role="img" aria-label="Laptura" />
        ) : (
          <LapturaWordmark className="h-9 w-auto" role="img" aria-label="Laptura" />
        )}
      </div>
    );
  }

  const stacked = Boolean(logo) && logo.layout === 'stacked';

  // Heights: a wide strip sits on the wordmark line (24px / 16px). A stacked
  // lockup needs more — the full 40px header on desktop, and 32px in the 40px
  // mobile bar (its largest line stays legible there; the smaller sponsor lines
  // read as the logo's shape, which is still better than dropping the artwork).
  const logoHeight = stacked ? (compact ? 'h-8' : 'h-10') : compact ? 'h-4' : 'h-6';

  const mark = logo ? (
    // Height-constrained with auto width: the aspect ratio is never altered.
    <img
      src={logo.src}
      alt={brand.wordmark}
      className={cn('w-auto object-contain', logoHeight)}
      data-testid="brandmark-logo"
    />
  ) : (
    // Type-set wordmark on the brand color — the "white on red" treatment the
    // brand books sanction for dark backgrounds.
    <span
      className={cn(
        'inline-flex items-center rounded bg-lap-accent text-white font-extrabold leading-none tracking-[0.08em]',
        compact ? 'text-[13px] px-1.5 py-1' : 'text-lg px-2 py-1.5'
      )}
    >
      {brand.wordmark}
    </span>
  );

  return (
    <div
      className={cn(
        'flex min-w-0',
        // Tall artwork eats the header height, so the tagline goes beside it.
        // Otherwise: stacked in the sidebar, inline in the 40px mobile bar.
        stacked || compact ? 'items-center gap-1.5' : 'flex-col items-start gap-0.5',
        className
      )}
      data-testid="brandmark-series"
    >
      {mark}
      <span
        className={cn(
          'text-lap-dim leading-none truncate max-w-full',
          compact ? 'text-[9px]' : 'text-[10px]'
        )}
      >
        {brand.tagline}
      </span>
    </div>
  );
}
